import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IMaskInput } from "react-imask";
import { useAuth } from "../../context/AuthContext";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import Logo from "../../components/Logo";
import { useBranding } from "../../context/BrandingContext";

interface IFormInput {
  name: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
}

interface PhoneMaskProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const PhoneMaskCustom = React.forwardRef<HTMLInputElement, PhoneMaskProps>(
  function PhoneMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="(00) 00000-0000"
        inputRef={ref}
        onAccept={(value: string) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  },
);

const schema = yup.object({
  name: yup.string().required("Nome é obrigatório"),
  email: yup.string().email("Email inválido").required("Email é obrigatório"),
  phone: yup.string().required("Telefone é obrigatório"),
  password: yup
    .string()
    .min(6, "Senha deve ter pelo menos 6 caracteres")
    .required("Senha é obrigatória"),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref("password")], "As senhas não coincidem")
    .required("Confirmação de senha é obrigatória"),
});

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { signUp } = useAuth();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { branding } = useBranding();

  // Atualiza o título e favicon da página
  useDocumentMeta();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IFormInput>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: IFormInput) => {
    try {
      setLoading(true);
      setError("");
      await signUp(data);
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };
  const backgroundGradient = `linear-gradient(to right, ${branding.colors.sidebarStart}, ${branding.colors.sidebarEnd})`;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
      }}
    >
      <Box
        sx={{
          minHeight: "100vh",
          width: "50%",
          background: backgroundGradient,
          padding: "44px",
          display: "none",
          flexDirection: "column",
          borderRadius: "0 28px 28px 0",
          height: "100%",
          overflow: "hidden",
          "@media (min-width: 768px)": {
            display: "flex",
          },
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Logo height={48} />
        </Box>
        <Box sx={{ my: "auto" }}>
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: branding.colors.sidebarStart,
              width: "max-content",
              borderRadius: "32px",
              color: theme.palette.primary.light,
            }}
          >
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Indique e ganhe
            </Typography>
          </Box>
          <Typography
            variant="h2"
            gutterBottom
            fontWeight="bold"
            color={theme.palette.common.white}
          >
            Ganhe dinheiro indicando
          </Typography>
          <Typography
            variant="h5"
            gutterBottom
            fontWeight="bold"
            color={alpha(theme.palette.common.white, 0.7)}
          >
            Cadastre-se, compartilhe seu link e ganhe comissões por cada cliente
            que fechar
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          borderRadius: "11px 0 0 11px",
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          lg: {
            width: "60%",
          },
        }}
      >
        <CardContent sx={{ p: 4, maxWidth: 500 }}>
          <Typography
            variant="h4"
            align="left"
            fontWeight="bold"
            color={branding.colors.sidebarStart}
            sx={{ mb: 1 }}
          >
            Crie sua conta
          </Typography>
          <Typography
            variant="body2"
            align="left"
            color={alpha(branding.colors.textDark, 0.7)}
            sx={{ mb: 3 }}
          >
            Preencha os campos abaixo para criar sua conta e comece a indicar
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Conta criada com sucesso! Redirecionando para o login...
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              label="Nome completo"
              margin="normal"
              {...register("name")}
              error={!!errors.name}
              helperText={errors.name?.message}
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              margin="normal"
              {...register("email")}
              error={!!errors.email}
              helperText={errors.email?.message}
            />

            <Controller
              name="phone"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Telefone"
                  margin="normal"
                  placeholder="(00) 00000-0000"
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  InputProps={{
                    inputComponent: PhoneMaskCustom as any,
                  }}
                />
              )}
            />

            <TextField
              fullWidth
              label="Senha"
              type="password"
              margin="normal"
              {...register("password")}
              error={!!errors.password}
              helperText={errors.password?.message}
            />

            <TextField
              fullWidth
              label="Confirmar senha"
              type="password"
              margin="normal"
              {...register("password_confirmation")}
              error={!!errors.password_confirmation}
              helperText={errors.password_confirmation?.message}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              size="large"
              disabled={loading || success}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Criar conta"}
            </Button>
          </form>

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2">
              Já tem conta?{" "}
              <Link
                component={RouterLink}
                to="/login"
                sx={{ color: branding.colors.sidebarStart }}
              >
                Faça login
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Box>
    </Box>
  );
};

export default SignUp;
